import React, { useState, useEffect, useRef } from "react";
import "./FinancialTable.less";
import service from "../../../services/service";
import { safeFormat } from "../../../utils/financialHelper";

// images
const Brookfield = new URL("../../../assets/Brookfield_4.png", import.meta.url)
  .href;
const Embassy = new URL("../../../assets/Embassy_3.png", import.meta.url).href;
const Nexus = new URL("../../../assets/Nexus_3.png", import.meta.url).href;
const Mindspace = new URL("../../../assets/Mindspace_1.png", import.meta.url)
  .href;

// logo
const BrookfieldLogo = new URL(
  "../../../assets/Brookfield_Logo.png",
  import.meta.url
).href;
const EmbassyLogo = new URL("../../../assets/EMBASSY_logo.png", import.meta.url)
  .href;
const NexusLogo = new URL(
  "../../../assets/nexusselecttrust-logo.png",
  import.meta.url
).href;
const MindspaceLogo = new URL(
  "../../../assets/Mindspace_Logo.jpg",
  import.meta.url
).href;

interface FinancialRecord {
  key: string;
  value?: string | number | boolean;
}

interface FinancialData {
  [key: string]: FinancialRecord;
}

interface Ratios {
  [key: string]: any;
}

interface CashFlows {
  [key: string]: FinancialRecord;
}

interface ProfitAndLoss {
  [key: string]: any;
}

interface BalanceSheet {
  [key: string]: any;
}

interface QuarterData {
  quarter: string;
  financialData: FinancialData;
  ratios: Ratios;
  cashFlows: CashFlows;
  pAndL: ProfitAndLoss;
  balanceSheet: BalanceSheet;
}

interface YearData {
  year: string;
  financialData: FinancialData;
  ratios: Ratios;
  cashFlows: CashFlows;
  pAndL: ProfitAndLoss;
  balanceSheet: BalanceSheet;
  quarters: QuarterData[];
}

interface ReitData {
  name: string;
  imagePath: string;
  years: YearData[];
}

type SectionKey =
  | "financialHighlights"
  | "ratios"
  | "cashFlows"
  | "pAndL"
  | "balanceSheet";

interface ExpandedSections {
  financialHighlights: boolean;
  ratios: boolean;
  cashFlows: boolean;
  pAndL: boolean;
  balanceSheet: boolean;
}

const FinancialTable: React.FC = () => {
  const displayName = "cmp-financial-table";
  const tableRef = useRef<HTMLTableElement>(null);
  const [viewMode, setViewMode] = useState<"yearly" | "quarterly">("yearly");
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([]);
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [availableQuarters, setAvailableQuarters] = useState<string[]>([]);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [reitsData, setReitsData] = useState<ReitData[]>([]);
  const [selectedReits, setSelectedReits] = useState<ReitData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReitSelector, setShowReitSelector] = useState(false);
  const [selectorAnchor, setSelectorAnchor] = useState<HTMLDivElement | null>(
    null
  );
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    financialHighlights: true,
    ratios: false,
    cashFlows: false,
    pAndL: false,
    balanceSheet: false,
  });
  const [financialDataKeys, setFinancialDataKeys] = useState<string[]>([]);
  const [ratiosKeys, setRatiosKeys] = useState<string[]>([]);
  const [cashFlowsKeys, setCashFlowsKeys] = useState<string[]>([]);
  const [pAndLKeys, setPAndLKeys] = useState<string[]>([]);
  const [balanceSheetKeys, setBalanceSheetKeys] = useState<string[]>([]);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  useEffect(() => {
    const calculateColumnWidths = () => {
      if (!tableRef.current) return;

      const headerCells = tableRef.current.querySelectorAll("thead tr th");
      const widths: number[] = [];

      headerCells.forEach((cell, index) => {
        if (index > 0) {
          widths.push((cell as HTMLElement).offsetWidth);
        }
      });

      setColumnWidths(widths);
    };

    if (!loading && selectedReits.length > 0 && selectedPeriods.length > 0) {
      setTimeout(calculateColumnWidths, 100);
    }

    window.addEventListener("resize", calculateColumnWidths);
    return () => window.removeEventListener("resize", calculateColumnWidths);
  }, [loading, selectedReits, selectedPeriods, viewMode]);

  const toggleReitSelector = (event: React.MouseEvent<HTMLDivElement>) => {
    if (showReitSelector) {
      setShowReitSelector(false);
      setSelectorAnchor(null);
    } else {
      setShowReitSelector(true);
      setSelectorAnchor(event.currentTarget);
    }
  };

  const addReit = (reit: ReitData) => {
    if (
      selectedReits.length < 4 &&
      !selectedReits.find((r) => r.name === reit.name)
    ) {
      setSelectedReits([...selectedReits, reit]);
    }
    setShowReitSelector(false);
    setSelectorAnchor(null);
  };

  const getImagesForReit = (reitName: string): string => {
    const lowerName = reitName.toLowerCase();
    if (lowerName.includes("brookfield")) {
      return Brookfield;
    } else if (lowerName.includes("embassy")) {
      return Embassy;
    } else if (lowerName.includes("nexus")) {
      return Nexus;
    } else if (lowerName.includes("mindspace")) {
      return Mindspace;
    }
    return Brookfield;
  };

  const getLogosForReit = (reitName: string): string => {
    const lowerName = reitName.toLowerCase();
    if (lowerName.includes("brookfield")) {
      return BrookfieldLogo;
    } else if (lowerName.includes("embassy")) {
      return EmbassyLogo;
    } else if (lowerName.includes("nexus")) {
      return NexusLogo;
    } else if (lowerName.includes("mindspace")) {
      return MindspaceLogo;
    }
    return BrookfieldLogo;
  };

  useEffect(() => {
    fetchCompareData();
  }, []);

  const fetchCompareData = async () => {
    try {
      setLoading(true);
      const response = await service.compareReit();
      setReitsData(response);

      setSelectedReits(response.slice(0, 3));

      if (response.length > 0 && response[0].years) {
        const years = response[0].years.map((y: YearData) => y.year).sort();
        setAvailableYears(years);

        const latestYear = years[years.length - 1];
        setSelectedYear(latestYear);

        const defaultYears = years.slice(-3);
        setSelectedPeriods(defaultYears);

        const latestYearData = response[0].years.find(
          (y: YearData) => y.year === latestYear
        );
        if (latestYearData?.quarters) {
          const quarters = latestYearData.quarters.map(
            (q: QuarterData) => q.quarter
          );
          setAvailableQuarters(quarters);
        }
      }

      if (
        response.length > 0 &&
        response[0].years &&
        response[0].years.length > 0
      ) {
        const yearData = response[0].years[0];

        if (yearData.financialData) {
          setFinancialDataKeys(Object.keys(yearData.financialData));
        }

        if (yearData.ratios) {
          setRatiosKeys(extractNestedKeys(yearData.ratios));
        }

        if (yearData.cashFlows) {
          setCashFlowsKeys(Object.keys(yearData.cashFlows));
        }

        if (yearData.pAndL) {
          setPAndLKeys(extractNestedKeys(yearData.pAndL));
        }

        if (yearData.balanceSheet) {
          setBalanceSheetKeys(extractNestedKeys(yearData.balanceSheet));
        }
      }
    } catch (error) {
      console.error("Error fetching compare data:", error);
    } finally {
      setLoading(false);
    }
  };

  const extractNestedKeys = (obj: any): string[] => {
    const keys: string[] = [];
    const traverse = (currentObj: any, prefix = "") => {
      Object.keys(currentObj).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        if (
          currentObj[key] &&
          typeof currentObj[key] === "object" &&
          "key" in currentObj[key]
        ) {
          keys.push(fullKey);
        } else if (
          currentObj[key] &&
          typeof currentObj[key] === "object" &&
          !Array.isArray(currentObj[key])
        ) {
          traverse(currentObj[key], fullKey);
        }
      });
    };
    traverse(obj);
    return keys;
  };

  const getNestedValue = (
    obj: any,
    path: string
  ): FinancialRecord | undefined => {
    const keys = path.split(".");
    let current = obj;
    for (const key of keys) {
      if (current && typeof current === "object" && key in current) {
        current = current[key];
      } else {
        return undefined;
      }
    }
    return current;
  };

  const handleViewModeChange = (mode: "yearly" | "quarterly") => {
    setViewMode(mode);

    if (mode === "yearly") {
      // Default: last 3 years
      const defaultYears = availableYears.slice(-3);
      setSelectedPeriods(defaultYears);
    } else {
      // Update quarters for the selected year
      updateQuartersForYear(selectedYear);

      // Default: select all quarters
      setSelectedPeriods([...availableQuarters]);
    }
  };

  const updateQuartersForYear = (year: string) => {
    if (reitsData.length > 0) {
      const yearData = reitsData[0].years.find(
        (y: YearData) => y.year === year
      );
      if (yearData?.quarters) {
        const quarters = yearData.quarters.map((q: QuarterData) => q.quarter);
        setAvailableQuarters(quarters);
      }
    }
  };

  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    updateQuartersForYear(year);
    const yearData = reitsData[0]?.years.find((y: YearData) => y.year === year);
    if (yearData?.quarters) {
      const quarters = yearData.quarters.map((q: QuarterData) => q.quarter);
      setSelectedPeriods(quarters);
    }
  };

  const removeReit = (index: number) => {
    setSelectedReits(selectedReits.filter((_, i) => i !== index));
  };

  const toggleSection = (section: SectionKey) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  const getDataForPeriod = (
    reit: ReitData,
    period: string,
    dataType:
      | "financialData"
      | "ratios"
      | "cashFlows"
      | "pAndL"
      | "balanceSheet"
  ) => {
    if (viewMode === "yearly") {
      const yearData = reit.years.find((y) => y.year === period);
      return yearData?.[dataType];
    } else {
      const yearData = reit.years.find((y) => y.year === selectedYear);
      if (yearData?.quarters) {
        const quarterData = yearData.quarters.find((q) => q.quarter === period);
        return quarterData?.[dataType];
      }
    }
    return null;
  };

  const formatValue = (record: FinancialRecord | undefined) => {
    if (!record) return "—";
    if (
      record.value === undefined ||
      record.value === null ||
      record.value === ""
    )
      return "—";
    return safeFormat(record.value);
  };

  const getLabel = (
    record: FinancialRecord | undefined,
    fallbackKey: string
  ) => {
    if (!record || !record.key) {
      return fallbackKey
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase());
    }
    return record.key;
  };

  const extractQuarterNumber = (quarterString: string): string => {
    const match = quarterString.match(/Q(\d)/);
    return match ? `Q${match[1]}` : quarterString;
  };

  const getReitCardWidth = (reitIndex: number): number => {
    if (columnWidths.length === 0) return 200;

    const startIdx = reitIndex * selectedPeriods.length;
    const endIdx = startIdx + selectedPeriods.length;
    const relevantWidths = columnWidths.slice(startIdx, endIdx);

    return relevantWidths.reduce((sum, width) => sum + width, 0);
  };

  const renderTableSection = (
    sectionKey: SectionKey,
    sectionTitle: string,
    dataKeys: string[],
    dataType:
      | "financialData"
      | "ratios"
      | "cashFlows"
      | "pAndL"
      | "balanceSheet"
  ) => (
    <div className={`${displayName}__section`}>
      <div
        className={`${displayName}__section-header`}
        onClick={() => toggleSection(sectionKey)}
      >
        <h3>{sectionTitle}</h3>
        <button className={`${displayName}__expand-btn`}>
          {expandedSections[sectionKey] ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#132c49"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="18 15 12 9 6 15" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#132c49"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          )}
        </button>
      </div>

      {expandedSections[sectionKey] && dataKeys.length > 0 && (
        <table
          className={`${displayName}__table`}
          ref={sectionKey === "financialHighlights" ? tableRef : undefined}
        >
          <thead className={`${displayName}__table-head`}>
            {/* REIT Images Row */}
            <tr className={`${displayName}__table-reit-row`}>
              <th className={`${displayName}__table-reit-header`}></th>
              {selectedReits.map((reit) => (
                <th
                  key={`${reit.name}__table-img`}
                  colSpan={selectedPeriods.length}
                  className={`${displayName}__table-reit-cell`}
                >
                  <div className={`${displayName}__table-reit-container`}>
                    <img
                      src={getLogosForReit(reit.name)}
                      alt={reit.name}
                      className={`${displayName}__table-reit-logo`}
                    />
                  </div>
                </th>
              ))}
            </tr>

            {/* Period Labels Row */}
            <tr className={`${displayName}__table-period-row`}>
              <th className={`${displayName}__table-period-header`}></th>
              {selectedReits.flatMap((reit) =>
                selectedPeriods.map((period) => (
                  <th
                    key={`${reit.name}-${period}`}
                    className={`${displayName}__table-period-cell`}
                  >
                    {viewMode === "yearly"
                      ? `FY ${period}`
                      : extractQuarterNumber(period)}
                  </th>
                ))
              )}
            </tr>
          </thead>

          <tbody>
            {dataKeys.map((key, rowIndex) => {
              const firstReit = selectedReits[0];
              const firstPeriod = selectedPeriods[0];
              const sampleData = getDataForPeriod(
                firstReit,
                firstPeriod,
                dataType
              );
              const sampleRecord =
                dataType === "financialData" || dataType === "cashFlows"
                  ? sampleData?.[key]
                  : getNestedValue(sampleData, key);

              return (
                <tr
                  key={key}
                  className={
                    rowIndex % 2 === 0 ? `${displayName}__row-even` : ""
                  }
                >
                  <td
                    className={`${displayName}__label-cell ${
                      getLabel(sampleRecord, key).startsWith("-")
                        ? "child-metric"
                        : "parent-metric"
                    }`}
                  >
                    {getLabel(sampleRecord, key).startsWith("-")
                      ? getLabel(sampleRecord, key).replace("-", "- ")
                      : getLabel(sampleRecord, key)}
                  </td>

                  {selectedReits.flatMap((reit) =>
                    selectedPeriods.map((period) => {
                      const data = getDataForPeriod(reit, period, dataType);
                      const record =
                        dataType === "financialData" || dataType === "cashFlows"
                          ? data?.[key]
                          : getNestedValue(data, key);
                      return (
                        <td key={`${reit.name}-${period}`}>
                          {formatValue(record)}
                        </td>
                      );
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  if (loading) {
    return <div className={displayName}>Loading...</div>;
  }

  return (
    <div className={displayName}>
      <div className={`${displayName}__top-section`}>
        {/* Row 1: Title */}
        <h2>
          Compare <span className={`${displayName}__title-italic`}>REITs</span>
        </h2>

        {/* Row 2: View Toggle + Filters */}
        <div className={`${displayName}__sticky-header`}>
          <div className={`${displayName}__controls-row`}>
            <div className={`${displayName}__view-toggle`}>
              <button
                className={`${displayName}__toggle-btn ${
                  viewMode === "yearly"
                    ? `${displayName}__toggle-btn--active`
                    : ""
                }`}
                onClick={() => handleViewModeChange("yearly")}
              >
                Yearly
              </button>
              <button
                className={`${displayName}__toggle-btn ${
                  viewMode === "quarterly"
                    ? `${displayName}__toggle-btn--active`
                    : ""
                }`}
                onClick={() => handleViewModeChange("quarterly")}
              >
                Quarterly
              </button>
            </div>

            <div className={`${displayName}__filters`}>
              {viewMode === "quarterly" && (
                <select
                  className={`${displayName}__year-dropdown`}
                  value={selectedYear}
                  onChange={(e) => handleYearChange(e.target.value)}
                >
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      FY {year}
                    </option>
                  ))}
                </select>
              )}

              {(viewMode === "yearly" ? availableYears : availableQuarters).map(
                (period) => (
                  <button
                    key={period}
                    className={`${displayName}__filter-btn ${
                      selectedPeriods.includes(period)
                        ? `${displayName}__filter-btn--active`
                        : ""
                    }`}
                    onClick={() => {
                      setSelectedPeriods((prev) => {
                        if (prev.includes(period)) {
                          // Prevent deselecting the last active period
                          if (prev.length === 1) return prev;
                          return prev.filter((p) => p !== period);
                        }

                        // Otherwise, add with max limit
                        const maxSelected = viewMode === "yearly" ? 3 : 4;
                        if (prev.length < maxSelected) {
                          return [...prev, period];
                        }

                        return prev; // don't add if limit reached
                      });
                    }}
                  >
                    {viewMode === "yearly"
                      ? `FY ${period}`
                      : extractQuarterNumber(period)}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Row 3: REIT cards */}
          <div className={`${displayName}__reits-cards`}>
            {selectedReits.map((reit, index) => (
              <div key={index} className={`${displayName}__reit-card`}>
                <div className={`${displayName}__reit-image-wrapper`}>
                  <img
                    src={getImagesForReit(reit.name)}
                    alt={reit.name}
                    className={`${displayName}__reit-image`}
                  />

                  {selectedReits.length > 1 && (
                    <button
                      className={`${displayName}__image-remove-btn`}
                      onClick={() => removeReit(index)}
                    >
                      ×
                    </button>
                  )}
                </div>
                <span className={`${displayName}__reit-name`}>{reit.name}</span>
              </div>
            ))}

            {selectedReits.length < 3 && (
              <div
                className={`${displayName}__add-reit-circle`}
                onClick={toggleReitSelector}
              >
                <div className={`${displayName}__add-icon`}>+</div>
                <span className={`${displayName}__add-label`}>Add REITs</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {showReitSelector && selectorAnchor && (
        <div
          className={`${displayName}__reit-selector`}
          style={{
            position: "absolute",
            top:
              selectorAnchor.getBoundingClientRect().bottom +
              window.scrollY +
              8,
            left: selectorAnchor.getBoundingClientRect().left + window.scrollX,
          }}
        >
          <ul>
            {reitsData.map((reit) => {
              const isDisabled = selectedReits.some(
                (r) => r.name === reit.name
              );
              return (
                <li key={reit.name}>
                  <button
                    disabled={isDisabled}
                    onClick={() => !isDisabled && addReit(reit)}
                    style={{
                      opacity: isDisabled ? 0.5 : 1,
                      cursor: isDisabled ? "not-allowed" : "pointer",
                    }}
                  >
                    <img
                      src={getImagesForReit(reit.name)}
                      alt={reit.name}
                      style={{ width: "40px", marginRight: "8px" }}
                    />
                    {reit.name}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className={`${displayName}__table-container`}>
        {renderTableSection(
          "financialHighlights",
          "Financial Highlights",
          financialDataKeys,
          "financialData"
        )}
        {renderTableSection("ratios", "Ratios", ratiosKeys, "ratios")}
        {renderTableSection(
          "cashFlows",
          "Cash Flows",
          cashFlowsKeys,
          "cashFlows"
        )}
        {renderTableSection("pAndL", "Profit & Loss", pAndLKeys, "pAndL")}
        {renderTableSection(
          "balanceSheet",
          "Balance Sheet",
          balanceSheetKeys,
          "balanceSheet"
        )}
      </div>
    </div>
  );
};

export default FinancialTable;
